import React from 'react';
import type { NormalizedIdeaCardData } from '@/lib/idea-card-types';
import { IdeaCard } from './IdeaCard';
import { getMarkdownIdeaComponents } from './markdown-components';
import { getIdeaCardPresentation } from './presentation';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../../../components/motion-primitives/accordion';
import { AccordionTriggerView } from '../../ui/AccordionTriggerView';
import { useAccordionHoverAndSelect } from '@/hooks/useAccordionHoverAndSelect';
import { HorizontalCardGroup } from '../HorizontalCardGroup';

import { useTranslation } from 'react-i18next';
import { formatIdeaCardBodyForMarkdown } from './presentation';

interface IdeaCardsProps {
  ideaBlocks: NormalizedIdeaCardData[];
  renderTextWithCitations?: (text: string) => React.ReactNode[];
}

/**
 * Check if a card has expandable content (body text or sections)
 */
const hasExpandableContent = (card: NormalizedIdeaCardData): boolean => {
  if (card.bodyMode === 'hidden') return false;

  if (card.bodyModel === 'sectioned_horizontal') {
    return card.displayBodySections && card.displayBodySections.length > 0;
  }

  const displayBody = formatIdeaCardBodyForMarkdown(card.displayBody);
  return Boolean(displayBody && displayBody.trim().length > 0);
};

export const IdeaCards: React.FC<IdeaCardsProps> = ({ ideaBlocks, renderTextWithCitations }) => {
  const cards = ideaBlocks ?? [];
  const { t } = useTranslation();
  const markdownIdeaComponentsByDensity = React.useMemo(
    () => ({
      compact: getMarkdownIdeaComponents(renderTextWithCitations || ((text: string) => [text]), t, { density: 'compact' }),
      relaxed: getMarkdownIdeaComponents(renderTextWithCitations || ((text: string) => [text]), t, { density: 'relaxed' }),
    }),
    [renderTextWithCitations, t]
  );

  const titleAccordion = useAccordionHoverAndSelect();
  const verticalAccordion = useAccordionHoverAndSelect();

  // Group cards by layout
  const titleCards = cards.filter(c => c.layout === 'title').slice(0, 5);
  const horizontalCards = cards.filter(c => c.layout === 'horizontal');

  // Group horizontal cards by their groupLabel
  const horizontalCardGroups = React.useMemo(() => {
    if (horizontalCards.length === 0) return [];

    const groups: Array<{ groupLabel?: string; cards: NormalizedIdeaCardData[] }> = [];
    let currentGroup: { groupLabel?: string; cards: NormalizedIdeaCardData[] } | null = null;

    for (const card of horizontalCards) {
      if (!currentGroup || currentGroup.groupLabel !== card.groupLabel) {
        currentGroup = { groupLabel: card.groupLabel, cards: [card] };
        groups.push(currentGroup);
      } else {
        currentGroup.cards.push(card);
      }
    }

    return groups;
  }, [horizontalCards]);

  const verticalCards = cards.filter(c => !c.layout || c.layout === 'vertical');

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className="w-full">
      {/* Title Cards - Always visible, no accordion */}
      {titleCards.length > 0 && (
        <Accordion
          className='flex w-full flex-col mb-4'
          transition={{ type: 'spring', stiffness: 220, damping: 18 }}
          variants={{
            expanded: {
              opacity: 1,
              scale: 1,
            },
            collapsed: {
              opacity: 0,
              scale: 0.7,
            },
          }}
          expandedValue={titleAccordion.expandedValue}
        >
          {titleCards.map((card, idx) => {
            const value = `title-${idx}`;
            const isExpandable = hasExpandableContent(card);
            return (
              <div
                key={value}
                onMouseEnter={isExpandable ? () => titleAccordion.handleMouseEnter(value) : undefined}
                onMouseLeave={isExpandable ? titleAccordion.handleMouseLeave : undefined}
              >
                <AccordionItem value={value}>
                  <AccordionTrigger
                    className='w-full py-0.5 text-left text-zinc-950 dark:text-zinc-50'
                    onClick={isExpandable ? () => titleAccordion.handleClick(value) : undefined}
                    disabled={!isExpandable}
                  >
                    <AccordionTriggerView
                      title={card.displayTitle || card.title}
                      index={idx}
                      isHighlighted={isExpandable && titleAccordion.isHighlighted(value)}
                      score={card.scoreRange || (typeof card.score === 'number' ? card.score : undefined)}
                      scoreUnit={card.scoreUnit}
                      scoreLabel={card.scoreLabel}
                      infoTooltip={card.infoTooltip}
                      showChevron={isExpandable}
                    />
                  </AccordionTrigger>
                  {isExpandable && (
                    <AccordionContent
                      className='origin-left'
                      onClick={() => titleAccordion.handleClick(value)}
                    >
                      <IdeaCard
                        card={card}
                        markdownComponents={markdownIdeaComponentsByDensity[getIdeaCardPresentation(card.layout).density]}
                      />
                    </AccordionContent>
                  )}
                </AccordionItem>
              </div>
            );
          })}
        </Accordion>
      )}

      {/* Horizontal Scroll Cards - grouped by groupLabel */}
      {horizontalCardGroups.map((group, groupIdx) => (
        <HorizontalCardGroup
          key={`group-${groupIdx}`}
          groupLabel={group.groupLabel}
          cards={group.cards}
          markdownIdeaComponentsByDensity={markdownIdeaComponentsByDensity}
        />
      ))}

      {/* Vertical Cards - Default accordion behavior */}
      {verticalCards.length > 0 && (
        <Accordion
          className='flex w-full flex-col mb-3'
          transition={{ type: 'spring', stiffness: 220, damping: 18 }}
          variants={{
            expanded: {
              opacity: 1,
              scale: 1,
            },
            collapsed: {
              opacity: 0,
              scale: 0.7,
            },
          }}
          expandedValue={verticalAccordion.expandedValue}
        >
          {verticalCards.map((card, idx) => {
            const value = `item-${idx}`;
            const isExpandable = hasExpandableContent(card);

            return (
              <div
                key={idx}
                onMouseEnter={isExpandable ? () => verticalAccordion.handleMouseEnter(value) : undefined}
                onMouseLeave={isExpandable ? verticalAccordion.handleMouseLeave : undefined}
              >
                <AccordionItem
                  value={value}
                >
                  <AccordionTrigger className='w-full py-0.5 text-left text-zinc-950 dark:text-zinc-50'
                    onClick={isExpandable ? () => verticalAccordion.handleClick(value) : undefined}
                    disabled={!isExpandable}
                  >
                    <AccordionTriggerView
                      title={card.displayTitle || card.title}
                      url={card.displayUrl}
                      index={titleCards.length + idx}
                      isHighlighted={isExpandable && verticalAccordion.isHighlighted(value)}
                      score={card.scoreRange || (typeof card.score === 'number' ? card.score : undefined)}
                      scoreUnit={card.scoreUnit}
                      scoreLabel={card.scoreLabel}
                      infoTooltip={card.infoTooltip}
                      showChevron={isExpandable}
                    />
                  </AccordionTrigger>
                  {isExpandable && (
                    <AccordionContent
                      className='origin-left'
                      onClick={() => verticalAccordion.handleClick(value)}
                    >
                      <IdeaCard
                        card={card}
                        markdownComponents={markdownIdeaComponentsByDensity[getIdeaCardPresentation(card.layout).density]}
                      />
                    </AccordionContent>
                  )}
                </AccordionItem>
              </div>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};
